<template>
  <div class="change-password-container">
    <div class="change-password-header">
      <span class="back" @click="$router.push('/settings')">&#8592;</span>
      <span class="title">修改密码</span>
    </div>
    
    <div class="change-password-content">
      <div class="form-group">
        <label class="form-label">新密码</label>
        <div class="input-group">
          <input
            v-model="formData.password"
            :type="showPassword ? 'text' : 'password'"
            class="form-input"
            placeholder="请输入新密码（6-16个字符，需包含大小写字母和数字）"
            maxlength="16"
          />
          <button type="button" class="password-toggle-btn" @click="showPassword = !showPassword">
            <span class="eye-icon">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
          </button>
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-label">确认密码</label>
        <div class="input-group">
          <input
            v-model="formData.confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            class="form-input"
            placeholder="请再次输入新密码"
            maxlength="16"
          />
          <button type="button" class="password-toggle-btn" @click="showConfirmPassword = !showConfirmPassword">
            <span class="eye-icon">{{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}</span>
          </button>
        </div>
      </div>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <button
        class="submit-btn"
        :class="{ disabled: !isFormValid || loading }"
        :disabled="!isFormValid || loading"
        @click="handleSubmit"
      >
        {{ loading ? '提交中...' : '确认修改' }}
      </button>
    </div>
  </div>
</template>

<script>
import { updatePassword, getUserInfo } from '@/api/user'

export default {
  name: 'ChangePasswordPage',
  data () {
    return {
      formData: {
        password: '',
        confirmPassword: ''
      },
      loading: false,
      error: null,
      showPassword: false,
      showConfirmPassword: false
    }
  },
  computed: {
    isFormValid () {
      const { password, confirmPassword } = this.formData
      // 密码长度6-16个字符
      if (password.length < 6 || password.length > 16) {
        return false
      }
      // 验证密码格式：必须包含大写字母、小写字母和数字
      if (!this.validatePasswordFormat(password)) {
        return false
      }
      // 两次密码必须一致
      if (password !== confirmPassword) {
        return false
      }
      return true
    }
  },
  watch: {
    'formData.password' () {
      const password = this.formData.password
      // 清除之前的错误提示（如果是因为密码格式或长度问题）
      if (this.error && (
        this.error === '密码必须包含大写字母、小写字母和数字' ||
        this.error === '密码长度必须在6-16个字符之间'
      )) {
        this.error = null
      }
      // 如果密码长度足够，检查格式
      if (password && password.length >= 6) {
        if (!this.validatePasswordFormat(password)) {
          this.error = '密码必须包含大写字母、小写字母和数字'
        }
      }
      // 如果确认密码已输入，检查是否一致
      if (this.formData.confirmPassword && password !== this.formData.confirmPassword) {
        this.error = '两次输入的密码不一致'
      }
    },
    'formData.confirmPassword' () {
      const { password, confirmPassword } = this.formData
      // 如果确认密码不为空且与新密码不一致，显示错误
      if (confirmPassword && password && password !== confirmPassword) {
        this.error = '两次输入的密码不一致'
      } else if (this.error === '两次输入的密码不一致') {
        // 如果密码一致了，清除错误提示
        if (password === confirmPassword) {
          // 如果密码格式不正确，显示格式错误
          if (password && password.length >= 6 && !this.validatePasswordFormat(password)) {
            this.error = '密码必须包含大写字母、小写字母和数字'
          } else {
            this.error = null
          }
        }
      }
    }
  },
  methods: {
    // 验证密码格式：必须包含大写字母、小写字母和数字
    validatePasswordFormat (password) {
      if (!password) return false
      // 检查是否包含大写字母
      const hasUpperCase = /[A-Z]/.test(password)
      // 检查是否包含小写字母
      const hasLowerCase = /[a-z]/.test(password)
      // 检查是否包含数字
      const hasNumber = /[0-9]/.test(password)
      return hasUpperCase && hasLowerCase && hasNumber
    },
    async handleSubmit () {
      if (!this.isFormValid) {
        return
      }
      
      const { password } = this.formData
      
      // 验证密码长度
      if (password.length < 6 || password.length > 16) {
        this.error = '密码长度必须在6-16个字符之间'
        return
      }
      
      // 验证密码格式
      if (!this.validatePasswordFormat(password)) {
        this.error = '密码必须包含大写字母、小写字母和数字'
        return
      }
      
      // 验证两次密码是否一致
      if (password !== this.formData.confirmPassword) {
        this.error = '两次输入的密码不一致'
        return
      }
      
      this.loading = true
      this.error = null
      
      try {
        // 先获取当前用户信息，获取用户名
        let username = ''
        try {
          const userInfo = await getUserInfo()
          username = userInfo?.data?.username || userInfo?.username || ''
          if (!username) {
            throw new Error('无法获取当前用户名')
          }
        } catch (error) {
          console.error('获取用户信息失败:', error)
          this.error = '获取用户信息失败，请稍后重试'
          this.loading = false
          return
        }
        
        // 使用正确的接口更新密码（需要同时提供用户名和密码）
        await updatePassword({ username, password })
        
        // 修改成功
        alert('密码修改成功，请重新登录')
        // 清除登录信息并跳转到登录页
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        this.$router.push('/login')
      } catch (error) {
        console.error('修改密码失败:', error)
        // 处理错误信息
        if (error?.response?.data) {
          const errorData = error.response.data
          this.error = errorData.message || errorData.msg || '修改密码失败，请稍后重试'
        } else {
          this.error = error?.message || '修改密码失败，请稍后重试'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.change-password-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
}

.change-password-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.change-password-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.change-password-content {
  padding: 24px 16px;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 15px;
  color: #222;
  font-weight: 500;
  margin-bottom: 12px;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  height: 48px;
  padding: 0 48px 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  color: #222;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.password-toggle-btn {
  position: absolute;
  right: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 16px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.password-toggle-btn:hover {
  opacity: 0.7;
}

.eye-icon {
  font-size: 18px;
  user-select: none;
}

.form-input:focus {
  outline: none;
  border-color: #3572e6;
}

.form-input::placeholder {
  color: #999;
}

.error-message {
  margin-top: -16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  color: #f56c6c;
  font-size: 14px;
}

.submit-btn {
  width: 100%;
  height: 48px;
  background: #3572e6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  margin-top: 8px;
}

.submit-btn:active:not(.disabled) {
  background: #2a5bc7;
  transform: scale(0.98);
}

.submit-btn.disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}
</style>

